'use client';

import { type IDefaultFormBuilderProps } from '@/components/Form/Fields/types';
import Label from '@/components/Label/Label';
import { FormControl, FormHelperText } from '@/components/field';
import InfoIconModal from '@/components/infoIcon/InfoIconModal';
import { RichText } from '@/components/richText/RichText';
import { FormSelect, type FormSelectOption } from '@/components/swdSelect';
import { hasContent, richtextToString } from '@/helper/RichText';
import { type FormSelectStoryblok } from '@/types/component-types-sb';
import classNames from 'classnames';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

export interface ISelectField extends FormSelectStoryblok, IDefaultFormBuilderProps {}

export const addEmptyOptionToOptionalSelect = (
  options: FormSelectOption[],
  required: boolean | undefined,
  emptyOptionLabel: string
): FormSelectOption[] => {
  if (required || options.some((option) => option.value === '')) {
    return options;
  }

  return [
    {
      value: '',
      label: emptyOptionLabel,
      'data-testid': 'form-builder-select-input-item-none',
    },
    ...options,
  ];
};

const SelectField: FC<ISelectField> = (props) => {
  const { t } = useTranslation();
  const configuredOptions: FormSelectOption[] = props.options.map((option, index) => ({
    value: option.value,
    label: option.label,
    'data-testid': 'form-builder-select-input-item' + Number(index + 1),
  }));
  const options = addEmptyOptionToOptionalSelect(configuredOptions, props.required, t('none'));

  return (
    <FormControl fullWidth error={props.isInvalid}>
      <FormSelect
        className={classNames(props.className, {
          selectHasInfoText: hasContent(props.infoText),
        })}
        density="default"
        disabled={props.disabled}
        displayEmpty={true}
        endAdornment={
          hasContent(props.infoText) ? (
            <InfoIconModal className="selectInfoText" trackingLabel={richtextToString(props.label)}>
              <RichText text={props.infoText} />
            </InfoIconModal>
          ) : undefined
        }
        error={props.isInvalid}
        id={props.id}
        inputProps={{ 'data-testid': 'form-builder-select-input' }}
        label={props.label && <Label text={props.label} />}
        labelId={`${props.id}Label`}
        name={props.field.name}
        onBlur={props.field.onBlur}
        onChange={props.field.onChange}
        options={options}
        placeholder={props.placeholder}
        readOnly={props.readOnly}
        required={props.required}
        size="medium"
        value={(props.field.value as string) ?? ''}
      />

      {props.isInvalid && props.errorMessage && <FormHelperText>{props.errorMessage}</FormHelperText>}
    </FormControl>
  );
};

export default SelectField;
